
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../base/Button';

export default function ServiceCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const services = [
    {
      id: 1,
      title: 'AI Property Staging',
      description: 'Transform empty properties with AI-generated furniture and decor to help buyers visualize their future home.',
      icon: 'ri-home-4-fill',
      image: 'https://public.readdy.ai/ai/img_res/205a4b8eb1fc9a53ad20fee2188b9d67.jpg',
      features: ['Virtual Furniture', 'Style Customization', 'Quick Turnaround', 'Cost Effective'],
      link: '/services/ai-staging'
    },
    {
      id: 2,
      title: 'AI Clone Service',
      description: 'Create your digital twin that works 24/7 to engage prospects and qualify leads automatically.',
      icon: 'ri-user-3-fill',
      image: 'https://readdy.ai/api/search-image?query=Professional%20real%20estate%20agent%20digital%20avatar%20hologram%20with%20modern%20technology%20interface%2C%20futuristic%20AI%20assistant%20representation%2C%20clean%20white%20background%2C%20professional%20lighting%2C%20high-tech%20atmosphere&width=600&height=400&seq=clone1&orientation=landscape',
      features: ['24/7 Lead Engagement', 'Voice & Text Responses', 'Personalized Interactions', 'Multi-language Support'],
      link: '/services/ai-clone'
    },
    {
      id: 3,
      title: 'AI Chatbots',
      description: 'Intelligent chatbots that handle inquiries, schedule viewings, and nurture leads while you focus on closing deals.',
      icon: 'ri-chat-3-fill',
      image: 'https://readdy.ai/api/search-image?query=Modern%20chatbot%20interface%20on%20smartphone%20screen%20showing%20real%20estate%20conversation%2C%20clean%20UI%20design%2C%20property%20listings%20visible%2C%20professional%20blue%20theme%2C%20minimalist%20background&width=600&height=400&seq=chatbot1&orientation=landscape',
      features: ['Instant Response', 'Lead Qualification', 'Appointment Scheduling', 'CRM Integration'],
      link: '/ai-tools/chatbots'
    },
    {
      id: 4,
      title: 'Social Media AI',
      description: 'Generate engaging content, schedule posts, and manage your social presence with AI-powered tools.',
      icon: 'ri-share-fill',
      image: 'https://readdy.ai/api/search-image?query=Social%20media%20content%20creation%20dashboard%20showing%20real%20estate%20posts%2C%20property%20photos%20with%20AI-generated%20captions%2C%20modern%20interface%20design%2C%20multiple%20platform%20icons%2C%20professional%20layout&width=600&height=400&seq=social1&orientation=landscape',
      features: ['Content Generation', 'Auto Scheduling', 'Engagement Analytics', 'Multi-platform Support'],
      link: '/ai-tools/social-media'
    },
    {
      id: 5,
      title: 'Lead Generation AI',
      description: 'Advanced AI algorithms identify and qualify high-quality leads from multiple sources automatically.',
      icon: 'ri-customer-service-2-fill',
      image: 'https://readdy.ai/api/search-image?query=AI%20lead%20generation%20dashboard%20with%20prospect%20analytics%2C%20lead%20scoring%20interface%2C%20real%20estate%20CRM%20system%2C%20modern%20data%20visualization%2C%20professional%20blue%20interface%20design&width=600&height=400&seq=leads1&orientation=landscape',
      features: ['Smart Lead Scoring', 'Multi-Channel Sourcing', 'Automated Follow-up', 'CRM Integration'],
      link: '/services/lead-generation'
    },
    {
      id: 6,
      title: 'Workflow Automation',
      description: 'Streamline your entire sales process with AI-powered automation that handles repetitive tasks.',
      icon: 'ri-settings-3-fill',
      image: 'https://readdy.ai/api/search-image?query=Automated%20workflow%20diagram%20with%20real%20estate%20processes%2C%20digital%20flowchart%20showing%20lead%20to%20sale%20journey%2C%20modern%20interface%20design%2C%20blue%20and%20white%20color%20scheme%2C%20professional%20presentation&width=600&height=400&seq=workflow1&orientation=landscape',
      features: ['Lead Nurturing', 'Follow-up Automation', 'Document Generation', 'Task Management'],
      link: '/ai-tools/automation'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % services.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [services.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % services.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + services.length) % services.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 font-sans">
            AI-Powered Services for Real Estate Success
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-sans">
            Transform your real estate business with our comprehensive suite of AI tools designed to automate, optimize, and accelerate your success.
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          {/* Carousel Container */}
          <div className="overflow-hidden rounded-2xl shadow-2xl bg-white">
            <div 
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {services.map((service) => (
                <div key={service.id} className="w-full flex-shrink-0">
                  <div className="bg-white p-8 lg:p-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                      {/* Content */}
                      <div className="space-y-6">
                        <div className="flex items-center space-x-4">
                          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                            <i className={`${service.icon} text-white text-2xl`}></i>
                          </div>
                          <h3 className="text-3xl font-bold text-gray-900 font-sans">{service.title}</h3>
                        </div>
                        
                        <p className="text-lg text-gray-600 leading-relaxed font-sans">
                          {service.description}
                        </p>

                        <div className="grid grid-cols-2 gap-4">
                          {service.features.map((feature, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <i className="ri-check-line text-green-500 text-lg"></i>
                              <span className="text-gray-700 font-medium font-sans">{feature}</span>
                            </div>
                          ))}
                        </div>

                        <div className="flex space-x-4">
                          <Link to={service.link}>
                            <Button size="lg" className="rounded-xl font-sans">
                              Learn More
                            </Button>
                          </Link>
                          <Link to="/demo">
                            <Button variant="outline" size="lg" className="rounded-xl font-sans">
                              Try Demo
                            </Button>
                          </Link>
                        </div>
                      </div>

                      {/* Image */}
                      <div className="relative group">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-80 object-cover rounded-xl shadow-lg group-hover:shadow-xl transition-shadow duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl"></div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3">
                            <p className="text-sm font-semibold text-gray-800 font-sans">{service.title}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl text-gray-700"></i>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10 cursor-pointer"
          >
            <i className="ri-arrow-right-line text-xl text-gray-700"></i>
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center space-x-2 mt-8">
            {services.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentSlide ? 'bg-blue-600 scale-125' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {/* Service Navigation Pills */}
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {services.map((service, index) => (
              <button
                key={service.id}
                onClick={() => goToSlide(index)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer font-sans ${
                  index === currentSlide
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-50 shadow-md'
                }`}
              >
                {service.title}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
